import { c as _c } from "react/compiler-runtime";
import { deleteEntitiesByFpIdInvestigationsByWorkflowIdArtifactsByKeyMutation, deleteOrgArtifactsByArtifactIdMutation, getEntitiesByFpIdOnboardingsByOnboardingIdDataOptions, getOrgArtifactsByArtifactId, getOrgArtifactsOptions } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { OrgArtifact } from '@onefootprint/request-types/dashboard';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import usePermissions from '@/hooks/use-permissions';
import { isPermissionError } from '@/utils/request-errors';
export type StoredBlock = {
  key: string;
  filename: string;
  title?: string;
  // Unset on artifact-backed blocks until `useBlockContents` fetches it.
  content?: string;
  metadata?: Record<string, unknown>;
  icon?: React.ComponentType<{
    className?: string;
  }>;
  artifactId?: string;
};
export type BlocksState = {
  blocks?: StoredBlock[];
  isLoading?: boolean;
  hasPermissionError?: boolean;
  refreshBlocks: () => void;
  deleteBlock: (block: StoredBlock) => void;
};
export const useBlocks = t0 => {
  const $ = _c(40);
  const {
    fpId,
    onboardingId,
    investigationId
  } = t0;
  const queryClient = useQueryClient();
  const showErrorToast = useRequestErrorToast();
  const {
    hasPermission
  } = usePermissions();
  let t1;
  if ($[0] !== hasPermission) {
    t1 = hasPermission("decrypt_artifacts");
    $[0] = hasPermission;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canDecryptArtifacts = t1;
  let t2;
  if ($[2] !== hasPermission) {
    t2 = hasPermission("decrypt_onboarding_data");
    $[2] = hasPermission;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const canDecryptOnboardingData = t2;
  let t3;
  if ($[4] !== fpId || $[5] !== onboardingId) {
    t3 = getEntitiesByFpIdOnboardingsByOnboardingIdDataOptions({
      path: {
        fpId,
        onboardingId
      }
    });
    $[4] = fpId;
    $[5] = onboardingId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const dataQueryOptions = t3;
  let t4;
  if ($[7] !== investigationId) {
    t4 = getOrgArtifactsOptions({
      query: {
        investigationId,
        pageSize: ARTIFACTS_PAGE_SIZE
      }
    });
    $[7] = investigationId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const artifactsQueryOptions = t4;
  const t5 = !!fpId && !!onboardingId && canDecryptOnboardingData;
  let t6;
  if ($[9] !== dataQueryOptions || $[10] !== t5) {
    t6 = {
      ...dataQueryOptions,
      enabled: t5,
      select: _temp
    };
    $[9] = dataQueryOptions;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const artifactsQuery = useQuery(t6);
  const t7 = !!investigationId && canDecryptArtifacts;
  let t8;
  if ($[12] !== artifactsQueryOptions || $[13] !== t7) {
    t8 = {
      ...artifactsQueryOptions,
      enabled: t7,
      select: _temp2
    };
    $[12] = artifactsQueryOptions;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const uiBlockArtifactsQuery = useQuery(t8);
  let t9;
  if ($[15] !== artifactsQueryOptions || $[16] !== dataQueryOptions || $[17] !== queryClient) {
    t9 = () => {
      queryClient.invalidateQueries({
        queryKey: dataQueryOptions.queryKey
      });
      queryClient.invalidateQueries({
        queryKey: artifactsQueryOptions.queryKey
      });
    };
    $[15] = artifactsQueryOptions;
    $[16] = dataQueryOptions;
    $[17] = queryClient;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const refreshBlocks = t9;
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = deleteEntitiesByFpIdInvestigationsByWorkflowIdArtifactsByKeyMutation();
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const deleteBlockMutation = useMutation(t10);
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = deleteOrgArtifactsByArtifactIdMutation();
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const deleteArtifactBlockMutation = useMutation(t11);
  let t12;
  if ($[21] !== deleteArtifactBlockMutation || $[22] !== deleteBlockMutation || $[23] !== fpId || $[24] !== onboardingId || $[25] !== refreshBlocks || $[26] !== showErrorToast) {
    t12 = block => {
      if (block.artifactId) {
        deleteArtifactBlockMutation.mutate({
          path: {
            artifactId: block.artifactId
          }
        }, {
          onSuccess: refreshBlocks,
          onError: showErrorToast
        });
        return;
      }
      if (!fpId || !onboardingId) {
        return;
      }
      deleteBlockMutation.mutate({
        path: {
          fpId,
          workflowId: onboardingId,
          key: block.key
        }
      }, {
        onSuccess: refreshBlocks,
        onError: showErrorToast
      });
    };
    $[21] = deleteArtifactBlockMutation;
    $[22] = deleteBlockMutation;
    $[23] = fpId;
    $[24] = onboardingId;
    $[25] = refreshBlocks;
    $[26] = showErrorToast;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const deleteBlock = t12;
  let t13;
  if ($[28] !== artifactsQuery.data || $[29] !== uiBlockArtifactsQuery.data) {
    const artifactBlocks = (uiBlockArtifactsQuery.data ?? []).map(artifactToStoredBlock).filter(_temp3);
    const legacyBlocks = (artifactsQuery.data ?? []).filter(_temp4).map(_temp5);
    const artifactKeys = new Set(artifactBlocks.map(_temp6));
    t13 = [...artifactBlocks, ...legacyBlocks.filter(block_2 => !artifactKeys.has(block_2.key))].sort(_temp7);
    $[28] = artifactsQuery.data;
    $[29] = uiBlockArtifactsQuery.data;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const blocks = t13;
  const t14 = artifactsQuery.isLoading || uiBlockArtifactsQuery.isLoading;
  let t15;
  if ($[31] !== artifactsQuery.error || $[32] !== canDecryptArtifacts) {
    t15 = !canDecryptArtifacts || isPermissionError(artifactsQuery.error);
    $[31] = artifactsQuery.error;
    $[32] = canDecryptArtifacts;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== blocks || $[35] !== deleteBlock || $[36] !== refreshBlocks || $[37] !== t14 || $[38] !== t15) {
    t16 = {
      blocks,
      isLoading: t14,
      hasPermissionError: t15,
      deleteBlock,
      refreshBlocks
    };
    $[34] = blocks;
    $[35] = deleteBlock;
    $[36] = refreshBlocks;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
};

// Split from `useBlocks` so the page reads block bytes only where the blocks render (the Generated
// Summary), not on every load: each read is a logged artifact fetch.
export const useBlockContents = blocks => {
  const $ = _c(7);
  const artifactIds = blocks.flatMap(_temp8);
  const contents = useQueries({
    queries: artifactIds.map(_temp9)
  });
  let t0;
  if ($[0] !== contents) {
    t0 = (artifactId_0, index) => [artifactId_0, contents[index]?.data];
    $[0] = contents;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const contentById = new Map(artifactIds.map(t0));
  const t1 = blocks.flatMap(block_0 => {
    const content = block_0.artifactId ? contentById.get(block_0.artifactId) : block_0.content;
    return content ? [{
      ...block_0,
      content
    }] : [];
  });
  let t2;
  if ($[2] !== contents) {
    t2 = contents.some(_temp10);
    $[2] = contents;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      blocks: t1,
      isLoading: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};

// Matches backend MAX_PAGE_SIZE for the org artifacts endpoint.
const ARTIFACTS_PAGE_SIZE = 100;
const isUiBlockKey = (key: string) => key.startsWith('ui_block_') && key.endsWith('.jsonl');
const isUiBlockArtifact = (artifact: OrgArtifact) => artifact.metadata.kind === 'ui_block';
const artifactToStoredBlock = (artifact: OrgArtifact): StoredBlock | undefined => {
  if (artifact.metadata.kind !== 'ui_block') return undefined;
  const {
    data
  } = artifact.metadata;
  return {
    key: data.key,
    filename: artifact.name,
    title: data.title ?? artifact.name,
    metadata: data.schemaVersion ? {
      schema_version: data.schemaVersion
    } : undefined,
    artifactId: artifact.id
  };
};
function _temp(res) {
  return res.data;
}
function _temp2(res_0) {
  return res_0.data.filter(isUiBlockArtifact);
}
function _temp3(block_0) {
  return !!block_0;
}
function _temp4(a) {
  return isUiBlockKey(a.key);
}
function _temp5(a_0) {
  return {
    key: a_0.key,
    filename: a_0.key,
    title: a_0.schema?.kind === "file" ? a_0.schema.data.name : undefined,
    content: typeof a_0.value === "string" ? a_0.value : JSON.stringify(a_0.value),
    metadata: a_0.schema?.kind === "file" ? a_0.schema.data.metadata : undefined
  };
}
function _temp6(block_1) {
  return block_1.key;
}
function _temp7(a_1, b) {
  return a_1.key.localeCompare(b.key);
}
function _temp8(block) {
  return block.artifactId ?? [];
}
function _temp9(artifactId) {
  return {
    queryKey: ["gen-ui-block-artifact-content", artifactId],
    queryFn: async () => {
      const response = await getOrgArtifactsByArtifactId({
        path: {
          artifactId
        },
        parseAs: "blob",
        throwOnError: true
      });
      return (response.data as Blob).text();
    }
  };
}
function _temp10(query) {
  return query.isLoading;
}